/**
 * PublicPasswordGate
 *
 * Dual-column password prompt for password-protected shares, mirroring the
 * onboarding / invitation screens: a branded map illustration on the left and
 * the password form on the right. On submit it verifies the password (sets the
 * short-lived per-share cookie) then loads the data.
 *
 * NOTE (cross-site constraint): the verify cookie is `SameSite=Lax`, so it is
 * NOT sent on cross-site iframe subrequests. Embedded password-gated shares on
 * a foreign origin therefore cannot unlock — we surface a clear warning when
 * the gate is shown inside an iframe.
 *
 * @module components/share/public/PublicPasswordGate
 */
import { memo, useState, type FormEvent } from 'react';
import { Alert, Button, PasswordInput, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { SplitScreenLayout } from '@/components/layout/SplitScreenLayout';
import { GateMobileLayout } from '@/components/layout/GateMobileLayout';
import { OnboardingSettingsBar } from '@/components/layout/OnboardingSettingsBar';
import { useDeviceLayout } from '@/hooks';

import { ShareGateLeftPanel, ShareLockIllustration } from '../shareGate';
import styles from './PublicPasswordGate.module.css';

interface PublicPasswordGateProps {
  hasError: boolean;
  isRateLimited: boolean;
  onSubmit: (password: string) => void;
}

/** True when the page is rendered inside an iframe (embed). */
function isEmbeddedInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access throws — that itself means we are framed.
    return true;
  }
}

export const PublicPasswordGate = memo(function PublicPasswordGate({
  hasError,
  isRateLimited,
  onSubmit,
}: PublicPasswordGateProps) {
  const { t } = useTranslation();
  const { isMobile } = useDeviceLayout();
  const [password, setPassword] = useState('');
  const framed = isEmbeddedInIframe();

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    if (password.length > 0) onSubmit(password);
  }

  const formContent = (
    <form className={styles.rightFormWrapper} onSubmit={handleSubmit}>
      <Stack gap="md">
        <Stack gap="3xs">
          <h2 className={styles.formTitle}>
            {t('share.public.password.title')}
          </h2>
          <Text size="sm" c="dimmed">
            {t('share.public.password.subtitle')}
          </Text>
        </Stack>

        {framed && (
          <Alert color="yellow" variant="light">
            {t('share.public.password.crossSiteWarning')}
          </Alert>
        )}

        {isRateLimited && (
          <Alert color="red" variant="light">
            {t('share.public.password.rateLimited')}
          </Alert>
        )}

        <PasswordInput
          label={t('share.public.password.label')}
          placeholder={t('share.public.password.placeholder')}
          value={password}
          onChange={(event) => setPassword(event.currentTarget.value)}
          error={hasError ? t('share.public.password.error') : undefined}
          autoFocus
        />

        <Button type="submit" disabled={password.length === 0} fullWidth>
          {t('share.public.password.submit')}
        </Button>
      </Stack>
    </form>
  );

  if (isMobile) {
    return <GateMobileLayout>{formContent}</GateMobileLayout>;
  }

  return (
    <SplitScreenLayout
      leftPanelBackground={<ShareLockIllustration />}
      leftPanel={<ShareGateLeftPanel variant="password" />}
      settingsBar={<OnboardingSettingsBar />}
      rightPanel={formContent}
    />
  );
});
